import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";

import momentTz from "moment-timezone";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import useConsultationContext from "hook/useConsultationContext";

import useDevices, { MEDIA_MODE } from "hook/useDevices";

import MediaModeSelector from "components/consultation/waiting-room/MediaModeSelector";
import VideoPreviewCard from "components/consultation/waiting-room/VideoPreviewCard";
import DeviceChecks from "components/consultation/waiting-room/DeviceChecks";
import FixGuideDialog from "components/consultation/waiting-room/FixGuideDialog";
import FeatureGuideDialog from "components/consultation/waiting-room/FeatureGuideDialog";

import {
  CONSULTATION_STAGE,
  resolvePatientConsultationStage,
} from "./utils/resolvePatientConsultationStage";
import JoinConsultationDialog from "components/consultation/waiting-room/JoinConsultationDialog";
import DeedButtons from "components/common/Elements/DeedButtons";

const resolveDisplayTimezone = () =>
  localStorage.getItem("Time-Zone") || momentTz.tz.guess() || "UTC";

const formatUtcDateTime = (value, timezone) => {
  if (!value) {
    return "Unavailable";
  }

  const parsedValue = momentTz.utc(value);

  if (!parsedValue.isValid()) {
    return "Unavailable";
  }

  return parsedValue.tz(timezone).format("DD MMM YYYY, hh:mm A");
};

const getResponseMessage = (error, fallbackMessage) =>
  error?.response?.data?.message ||
  error?.response?.data?.error?.message ||
  error?.message ||
  fallbackMessage;

const UNAVAILABLE_MESSAGES = {
  PHYSICAL_APPOINTMENT:
    "This is a physical appointment. Online consultation is not available.",

  APPOINTMENT_CANCELED: "This appointment has been canceled.",

  APPOINTMENT_COMPLETED: "This consultation has already been completed.",

  INVALID_APPOINTMENT_STATUS:
    "This appointment is not currently available for online consultation.",

  INVALID_APPOINTMENT_SLOT_TIME:
    "The consultation time is unavailable or invalid.",

  UNSUPPORTED_PROFESSION_TYPE:
    "Consultation timing is not configured for this health professional.",

  CONSULTATION_HARD_ENDED: "The consultation time has ended.",
};

const PatientWaitingRoom = () => {
  const navigate = useNavigate();

  const params = useParams();

  const location = useLocation();

  const appointmentId = params.appointmentId || params.id || null;

  const timezone = resolveDisplayTimezone();

  const providerName = location.state?.providerName || "Health professional";

  /*
   * =========================================================
   * CONSULTATION CONTEXT
   * =========================================================
   */

  const { consultationContext, loading, error, refresh } =
    useConsultationContext({
      appointmentId,
      enabled: Boolean(appointmentId),
    });

  const consultationStage = useMemo(
    () => resolvePatientConsultationStage(consultationContext),
    [consultationContext],
  );

  const timing = consultationContext?.timing;

  const consultationAvailable =
    consultationContext?.consultationAvailable === true;

  const waitingRoomAccessible = timing?.waitingRoomAccessible === true;

  const joinable = timing?.joinable === true;

  const hardEnded =
    timing?.hardEnded === true ||
    consultationStage === CONSULTATION_STAGE.HARD_ENDED;

  /*
   * =========================================================
   * DEVICE PREPARATION
   * =========================================================
   */

  const videoRef = useRef(null);

  const devices = useDevices(videoRef);

  /*
   * Audio & Video is the default selection
   * for easier UX.
   *
   * No camera or microphone access happens
   * until the user starts the optional
   * device check or enters the consultation.
   */
  const [mediaMode, setMediaMode] = useState(MEDIA_MODE.AUDIO_VIDEO);

  const [deviceCheckStarted, setDeviceCheckStarted] = useState(false);

  const [deviceChecking, setDeviceChecking] = useState(false);

  const [deviceResult, setDeviceResult] = useState(null);

  const [fixGuideOpen, setFixGuideOpen] = useState(false);

  const [featureGuideOpen, setFeatureGuideOpen] = useState(false);

  const [joinChecking, setJoinChecking] = useState(false);

  const [joinError, setJoinError] = useState("");

  const [joinDialogOpen, setJoinDialogOpen] = useState(false);

  /*
   * Stop local browser media when
   * leaving the Waiting Room.
   */
  useEffect(() => {
    return () => {
      devices.stopDevices();
    };
  }, [devices.stopDevices]);

  /*
   * =========================================================
   * DEVICE READINESS
   * =========================================================
   */

  const cameraRequired = mediaMode === MEDIA_MODE.AUDIO_VIDEO;

  const cameraReady = !cameraRequired || devices.camStatus === "pass";

  const microphoneReady = devices.micStatus === "pass";

  const speakerReady = devices.speakerStatus === "pass";

  const hasDeviceIssue =
    deviceCheckStarted &&
    ((cameraRequired && devices.camStatus === "fail") ||
      devices.micStatus === "fail" ||
      devices.speakerStatus === "fail");

  const devicesReady =
    deviceCheckStarted && cameraReady && microphoneReady && speakerReady;

  const skippedDeviceCheck = !deviceCheckStarted;

  const deviceCheckHasIssues = deviceCheckStarted && !devicesReady;

  /*
   * =========================================================
   * NAVIGATION
   * =========================================================
   */

  const handleBackToAppointment = () => {
    devices.stopDevices();

    if (!appointmentId) {
      navigate("/appointments");

      return;
    }

    navigate(`/appointment-consultation/${encodeURIComponent(appointmentId)}`);
  };

  /*
   * =========================================================
   * CONSULTATION RETRY
   * =========================================================
   */

  const handleRetry = async () => {
    try {
      await refresh();
    } catch (refreshError) {
      /*
       * useConsultationContext
       * stores the error.
       */
    }
  };

  /*
   * =========================================================
   * MEDIA MODE
   * =========================================================
   */

  const handleMediaModeChange = (nextMode) => {
    if (
      nextMode !== MEDIA_MODE.AUDIO_VIDEO &&
      nextMode !== MEDIA_MODE.AUDIO_ONLY
    ) {
      return;
    }

    /*
     * Changing mode destroys the previous
     * local stream.
     *
     * Privacy Mode therefore removes the
     * camera track completely rather than
     * merely hiding the preview.
     */
    devices.stopDevices();

    setMediaMode(nextMode);

    setDeviceCheckStarted(false);

    setDeviceChecking(false);

    setDeviceResult(null);

    setJoinError("");

    setFixGuideOpen(false);
  };

  /*
   * =========================================================
   * START DEVICE CHECK
   * =========================================================
   */

  const startDeviceCheck = async (selectedMode = mediaMode) => {
    if (!selectedMode) {
      return null;
    }

    setDeviceChecking(true);

    setDeviceResult(null);

    setJoinError("");

    try {
      const result = await devices.startDevices({
        mediaMode: selectedMode,
      });

      setDeviceResult(result);

      setDeviceCheckStarted(true);

      return result;
    } finally {
      setDeviceChecking(false);
    }
  };

  /*
   * Explicit user action:
   *
   * failed Audio + Video
   * → user chooses Audio Only
   * → destroy previous stream
   * → request microphone only
   */
  const handleUseAudioOnly = async () => {
    devices.stopDevices();

    setMediaMode(MEDIA_MODE.AUDIO_ONLY);

    setDeviceCheckStarted(false);

    setDeviceResult(null);

    setFixGuideOpen(false);

    await startDeviceCheck(MEDIA_MODE.AUDIO_ONLY);
  };

  /*
   * =========================================================
   * DEVICE RETRY
   * =========================================================
   */

  const handleDeviceRetry = async () => {
    if (!mediaMode) {
      return;
    }

    const result = await startDeviceCheck(mediaMode);

    if (result?.success) {
      setFixGuideOpen(false);
    }
  };

  /*
   * =========================================================
   * JOIN CONSULTATION
   * =========================================================
   *
   * Still a checkpoint.
   *
   * We revalidate the backend immediately
   * before allowing transition to /call.
   */

  const handleJoinConsultation = async () => {
    setJoinError("");

    /*
     * Local device readiness is UX
     * preparation.
     *
     * Backend consultation context remains
     * the timing/access authority.
     */

    setJoinChecking(true);

    try {
      const freshContext = await refresh({
        silent: true,
      });

      const freshTiming = freshContext?.timing;

      const canJoin =
        freshContext?.consultationAvailable === true &&
        freshTiming?.waitingRoomAccessible === true &&
        freshTiming?.joinable === true &&
        freshTiming?.hardEnded !== true;

      if (!canJoin) {
        setJoinError("The consultation is not currently available to join.");

        return;
      }

      /*
       * Consultation access is valid.
       *
       * The Join dialog now handles the
       * final privacy/conduct acknowledgement
       * before the patient enters /call.
       */
      setJoinDialogOpen(true);
    } catch (joinRequestError) {
      setJoinError(
        getResponseMessage(
          joinRequestError,
          "Unable to verify consultation access. Please try again.",
        ),
      );
    } finally {
      setJoinChecking(false);
    }
  };

  /*
   * =========================================================
   * LOADING
   * =========================================================
   */

  if (loading && !consultationContext) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress />

          <Typography variant="body2" color="text.secondary">
            Loading waiting room...
          </Typography>
        </Stack>
      </Box>
    );
  }

  /*
   * =========================================================
   * MISSING APPOINTMENT ID
   * =========================================================
   */

  if (!appointmentId) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={1}
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5">Waiting Room</Typography>

            <Alert severity="error">Appointment ID is unavailable.</Alert>

            <Button
              variant="outlined"
              onClick={() => navigate("/appointments")}
              sx={{
                alignSelf: "flex-start",
              }}
            >
              Back to Appointments
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * API ERROR
   * =========================================================
   */

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={1}
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5">Waiting Room</Typography>

            <Alert severity="error">
              {getResponseMessage(
                error,
                "Unable to verify waiting room access.",
              )}
            </Alert>

            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              spacing={1}
            >
              <Button
                variant="contained"
                startIcon={<RefreshOutlinedIcon />}
                onClick={handleRetry}
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={handleBackToAppointment}
              >
                Back to Appointment Details
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * HARD ENDED
   * =========================================================
   */

  if (hardEnded) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={1}
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5">Waiting Room</Typography>

            <Alert severity="info">The consultation time has ended.</Alert>

            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackToAppointment}
              sx={{
                alignSelf: "flex-start",
              }}
            >
              Back to Appointment Details
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * UNAVAILABLE
   * =========================================================
   */

  if (
    consultationStage === CONSULTATION_STAGE.UNAVAILABLE ||
    !consultationAvailable
  ) {
    const unavailableMessage =
      UNAVAILABLE_MESSAGES[consultationContext?.reason] ||
      "This consultation is currently unavailable.";

    return (
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={1}
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5">Waiting Room</Typography>

            <Alert severity="warning">{unavailableMessage}</Alert>

            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackToAppointment}
              sx={{
                alignSelf: "flex-start",
              }}
            >
              Back to Appointment Details
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * WAITING ROOM NOT OPEN
   * =========================================================
   */

  if (
    consultationStage === CONSULTATION_STAGE.SCHEDULED ||
    !waitingRoomAccessible
  ) {
    return (
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={1}
          sx={{
            maxWidth: 700,
            mx: "auto",
            p: 3,
            borderRadius: 3,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="h5">Waiting Room</Typography>

            <Alert severity="info">The waiting room is not open yet.</Alert>

            <Typography variant="body2" color="text.secondary">
              Waiting room opens at{" "}
              <strong>
                {formatUtcDateTime(timing?.waitingOpensAt, timezone)}
              </strong>
            </Typography>

            <Button
              variant="outlined"
              startIcon={<ArrowBackOutlinedIcon />}
              onClick={handleBackToAppointment}
              sx={{
                alignSelf: "flex-start",
              }}
            >
              Back to Appointment Details
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  /*
   * =========================================================
   * REAL WAITING ROOM
   * =========================================================
   */

  return (
    <>
      <FixGuideDialog
        open={fixGuideOpen}
        errorType={devices.deviceError}
        retrying={deviceChecking}
        onClose={() => setFixGuideOpen(false)}
        onRetry={handleDeviceRetry}
      />

      <FeatureGuideDialog
        open={featureGuideOpen}
        onClose={() => setFeatureGuideOpen(false)}
      />

      <JoinConsultationDialog
        open={joinDialogOpen}
        skippedDeviceCheck={skippedDeviceCheck}
        deviceCheckHasIssues={deviceCheckHasIssues}
        onClose={() => setJoinDialogOpen(false)}
        onReturnToDeviceCheck={() => {
          setJoinDialogOpen(false);

          /*
           * Later we can scroll automatically
           * to the device-check section.
           */
        }}
        onEnterConsultation={() => {
          /*
           * The dialog calls this only after
           * the user accepts the privacy /
           * conduct acknowledgement.
           *
           * This state is intentionally
           * single-use. PatientConsultationRoom
           * consumes and removes it.
           */
          const consultationEntry = {
            enteredFromWaitingRoom: true,
            privacyAccepted: true,
            mediaMode,
            providerName,
          };

          /*
           * Destroy Waiting Room media before
           * Agora creates its own tracks.
           */
          devices.stopDevices();

          setJoinDialogOpen(false);

          navigate(
            `/appointment-consultation/${encodeURIComponent(
              appointmentId,
            )}/call`,
            {
              state: consultationEntry,
            },
          );
        }}
      />

      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          p: {
            xs: 1.5,
            sm: 2,
          },
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            maxWidth: 820,
            mx: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              // gap: 1,
            }}
          >
            <Box>
              <Button
                variant="outlined"
                startIcon={<ArrowBackOutlinedIcon />}
                onClick={handleBackToAppointment}
              >
                Back
              </Button>

              <h3 style={{ marginTop: "12px !important" }}>
                Consultation Waiting Room
              </h3>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0,
                }}
              >
                Appointment ID: {appointmentId}
              </Typography>
            </Box>

            <Button
              size="small"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setFeatureGuideOpen(true)}
            >
              Guide
            </Button>
          </Box>

          <Paper
            elevation={1}
            sx={{
              p: {
                xs: 2,
                sm: 3,
              },
              borderRadius: 3,
            }}
          >
            <Stack spacing={2}>
              {joinable ? (
                <Alert severity="success">
                  Your consultation is ready to join.
                </Alert>
              ) : (
                <Alert severity="info">
                  You are in the waiting room. Your consultation will become
                  available at the scheduled start time.
                </Alert>
              )}

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={2}
              >
                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Consultation starts
                  </Typography>

                  <Typography variant="body1" fontWeight={600}>
                    {formatUtcDateTime(timing?.slotAt, timezone)}
                  </Typography>
                </Box>

                <Box flex={1}>
                  <Typography variant="caption" color="text.secondary">
                    Consultation access ends
                  </Typography>

                  <Typography variant="body1" fontWeight={600}>
                    {formatUtcDateTime(timing?.hardEndsAt, timezone)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {/* Mode selection */}

          <Paper
            elevation={1}
            sx={{
              p: {
                xs: 2,
                sm: 3,
              },
              borderRadius: 3,
            }}
          >
            <Stack spacing={2}>
              <MediaModeSelector
                value={mediaMode || ""}
                onChange={handleMediaModeChange}
                disabled={deviceChecking}
              />

              {!deviceCheckStarted && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={
                    mediaMode === MEDIA_MODE.AUDIO_ONLY ? (
                      <PrivacyTipOutlinedIcon />
                    ) : (
                      <DevicesOutlinedIcon />
                    )
                  }
                  disabled={deviceChecking}
                  onClick={() => startDeviceCheck()}
                >
                  {deviceChecking
                    ? "Checking Devices..."
                    : mediaMode === MEDIA_MODE.AUDIO_ONLY
                      ? "Start Audio Check"
                      : "Start Camera & Audio Check"}
                </Button>
              )}
            </Stack>
          </Paper>

          {/* Device check result */}

          {deviceCheckStarted && (
            <>
              {deviceResult?.success === false &&
                deviceResult?.audioOnlyAvailable === true &&
                mediaMode === MEDIA_MODE.AUDIO_VIDEO && (
                  <Alert
                    severity="warning"
                    action={
                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={0.5}
                      >
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => setFixGuideOpen(true)}
                        >
                          How to Fix
                        </Button>

                        <Button
                          color="inherit"
                          size="small"
                          onClick={handleUseAudioOnly}
                        >
                          Use Audio Only
                        </Button>
                      </Stack>
                    }
                  >
                    Your camera is unavailable, but your microphone is working.
                    You can fix the camera or continue using Audio Only (Privacy
                    Mode).
                  </Alert>
                )}

              {deviceResult?.success === false &&
                !deviceResult?.audioOnlyAvailable && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => setFixGuideOpen(true)}
                      >
                        How to Fix
                      </Button>
                    }
                  >
                    The required consultation devices are not available. Please
                    fix the issue and try again.
                  </Alert>
                )}

              {mediaMode === MEDIA_MODE.AUDIO_ONLY &&
                devices.micStatus === "pass" && (
                  <Alert severity="info">
                    Privacy Mode is active. Your camera is not being accessed.
                  </Alert>
                )}

              <VideoPreviewCard
                videoRef={videoRef}
                mediaMode={mediaMode}
                devices={devices}
              />

              <DeviceChecks
                devices={devices}
                mediaMode={mediaMode}
                hasIssue={hasDeviceIssue}
                allGreen={devicesReady}
                onFix={() => setFixGuideOpen(true)}
                showNetwork={false}
              />
            </>
          )}

          {/* Join / Back */}

          <Paper
            elevation={1}
            sx={{
              p: {
                xs: 2,
                sm: 3,
              },
              borderRadius: 3,
            }}
          >
            <Stack spacing={2}>
              {joinError && <Alert severity="error">{joinError}</Alert>}

              {!deviceCheckStarted && (
                <Alert severity="info">
                  Device check is optional, but recommended before joining.
                </Alert>
              )}

              {deviceCheckStarted && !devicesReady && (
                <Alert severity="warning">
                  Some device checks need attention. You can fix them before
                  joining.
                </Alert>
              )}

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={1.5}
                justifyContent="space-between"
              >
                <DeedButtons
                  variant="contained"
                  size="large"
                  // disabled={!joinable || !devicesReady || joinChecking}
                  disabled={!joinable || joinChecking}
                  onClick={handleJoinConsultation}
                   sx={{
                    width: "auto",
                    minWidth: {
                      xs: 170,
                      sm: 180,
                    },
                    height: {
                      xs: 40,
                      sm: 44,
                    },
                    px: {
                      xs: 2,
                      sm: 2.5,
                    },
                    py: 0.75,
                    fontSize: {
                      xs: "0.9rem",
                      sm: "0.95rem",
                    },
                    borderRadius: "20px !important",
                    whiteSpace: "nowrap",
                  }}
                >
                  {/* {joinChecking
                    ? "Verifying..."
                    : !joinable
                      ? "Waiting for Start Time"
                      : !devicesReady
                        ? "Complete Device Check"
                        : "Join Consultation"} */}
                  {joinChecking
                    ? "Verifying..."
                    : !joinable
                      ? "Waiting for Start Time"
                      : "Join Consultation"}
                </DeedButtons>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </>
  );
};

export default PatientWaitingRoom;
